/**
 * Server / legacy APIs may return 0–100 or 0–1 — kept for any optional server scores.
 */
export function normalizeSpeechScorePercent(raw: unknown): number {
  if (raw === null || raw === undefined) return 0;
  const n = typeof raw === "string" ? parseFloat(raw.trim()) : Number(raw);
  if (Number.isNaN(n)) return 0;
  if (n >= 0 && n <= 1) return Math.round(n * 100);
  return Math.round(Math.min(100, Math.max(0, n)));
}

const STOP_WORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "but",
  "of",
  "to",
  "in",
  "on",
  "at",
  "by",
  "for",
  "with",
  "as",
  "is",
  "was",
  "are",
  "were",
  "be",
  "been",
  "being",
  "have",
  "has",
  "had",
  "do",
  "does",
  "did",
  "will",
  "would",
  "could",
  "should",
  "may",
  "might",
  "must",
  "can",
  "this",
  "that",
  "these",
  "those",
  "it",
  "its",
  "from",
  "into",
  "about",
  "than",
  "then",
  "so",
  "if",
  "not",
  "no",
  "yes",
  "your",
  "our",
  "their",
  "his",
  "her",
]);

function tokenize(s: string): string[] {
  const cleaned = s
    .toLowerCase()
    .replace(/[^a-z0-9'\s-]/gi, " ")
    .split(/\s+/)
    .map((w) => w.replace(/^['-]+|['-]+$/g, ""))
    .filter(Boolean);
  return cleaned;
}

function wordWeight(w: string): number {
  return STOP_WORDS.has(w) ? 1 : 3;
}

/** One word is a stem/prefix of the other (handles -ing, -ed, plurals). */
function prefixStemMatch(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.length < 2 || b.length < 2) return false;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return long.startsWith(short);
}

export type WordMatchStatus = "ok" | "partial" | "miss";

export interface WeightedMatchResult {
  score: number;
  /** One entry per word in the target phrase (for UI chips) */
  details: { word: string; status: WordMatchStatus }[];
}

/**
 * Weighted word match: stop words weight 1, content words weight 3.
 * Partial credit 0.75× when prefix/stem match. Score = (weighted hits / total weight) × 100.
 */
export function weightedPhraseMatch(phrase: string, transcript: string): WeightedMatchResult {
  const ref = tokenize(phrase);
  const hyp = tokenize(transcript);
  if (ref.length === 0) {
    return { score: 0, details: [] };
  }

  const totalWeight = ref.reduce((s, w) => s + wordWeight(w), 0);
  let hypIndex = 0;
  let weightedHits = 0;
  const details: { word: string; status: WordMatchStatus }[] = [];

  for (const rw of ref) {
    const w = wordWeight(rw);
    let matched = false;

    for (let j = hypIndex; j < hyp.length; j++) {
      if (hyp[j] === rw) {
        weightedHits += w;
        hypIndex = j + 1;
        details.push({ word: rw, status: "ok" });
        matched = true;
        break;
      }
    }
    if (matched) continue;

    for (let j = hypIndex; j < hyp.length; j++) {
      if (prefixStemMatch(rw, hyp[j])) {
        weightedHits += 0.75 * w;
        hypIndex = j + 1;
        details.push({ word: rw, status: "partial" });
        matched = true;
        break;
      }
    }
    if (!matched) {
      details.push({ word: rw, status: "miss" });
    }
  }

  const score = Math.round((weightedHits / totalWeight) * 100);
  return {
    score: Math.min(100, Math.max(0, score)),
    details,
  };
}
